/**
 * Parent → Report Detail
 *
 * Renders a single approved report in the exact Aanya structure shown to
 * observers/principals. Read-only for parents, with the principal's
 * parent-facing note rendered prominently at the top.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageSquare, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import ParentNav from '../components/ParentNav';
import DaysInsightsReport from '../components/DaysInsightsReport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HIGHLIGHT_BG = {
  yellow: 'bg-yellow-50 border-l-4 border-yellow-400',
  green:  'bg-emerald-50 border-l-4 border-emerald-400',
  pink:   'bg-pink-50 border-l-4 border-pink-400',
  blue:   'bg-blue-50 border-l-4 border-blue-400',
};
const HIGHLIGHT_DOT = {
  yellow: 'bg-yellow-400', green: 'bg-emerald-400', pink: 'bg-pink-400', blue: 'bg-blue-400',
};

const PrincipalRemarks = ({ annotations }) => {
  const entries = Object.entries(annotations || {}).filter(
    ([, a]) => a && (a.highlight_color || a.note),
  );
  if (!entries.length) return null;

  return (
    <Card className="mt-8 border-sanjaya-navy/25 shadow-sm" data-testid="parent-principal-remarks">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sanjaya-navy text-base">
          📋 Principal&apos;s Remarks
        </CardTitle>
        <p className="text-sm text-slate-500 mt-0.5">
          Your child&apos;s principal reviewed this report and left the following observations.
        </p>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {entries.map(([key, ann]) => (
          <div
            key={key}
            className={`p-3 rounded-r-lg ${ann.highlight_color ? HIGHLIGHT_BG[ann.highlight_color] : 'border-l-4 border-slate-300 bg-slate-50'}`}
          >
            <div className="flex items-center gap-2 mb-1">
              {ann.highlight_color && (
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${HIGHLIGHT_DOT[ann.highlight_color]}`} />
              )}
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                {ann.section_title || key.replace(/_/g, ' ')}
              </p>
            </div>
            {ann.note ? (
              <p className="text-sm text-slate-700 leading-relaxed flex items-start gap-1.5">
                <span className="mt-0.5">📌</span>
                <span className="italic">{ann.note}</span>
              </p>
            ) : (
              <p className="text-xs text-slate-400 italic">Section highlighted for attention</p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const ParentReportDetail = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('parent_token');
      if (!token) { navigate('/parent/login'); return; }
      const { data } = await axios.get(
        `${BACKEND_URL}/api/parent/report/${reportId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setReport(data.report);
    } catch (e) {
      toast({
        title: 'Could not load report',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [reportId, navigate, toast]);

  useEffect(() => { loadReport(); }, [loadReport]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-rose-50" data-testid="parent-report-detail">
      <ParentNav />

      <div className="pt-20 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <Button variant="ghost" onClick={() => navigate('/parent/daily-insight')} data-testid="back-to-insights">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Insights
            </Button>
            <Button variant="outline" size="sm" onClick={loadReport}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" />
            </div>
          )}

          {!loading && report && (
            <>
              {/* Principal's general note (brief approval note shown at top) */}
              {report.principal_feedback_for_parent && (
                <Card className="mb-6 border-amber-200 bg-amber-50" data-testid="parent-principal-note">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-amber-800 text-base">
                      <MessageSquare className="w-5 h-5" /> A note from the principal
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-amber-900 italic leading-relaxed">
                      &ldquo;{report.principal_feedback_for_parent}&rdquo;
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Full report */}
              <DaysInsightsReport data={report.report_data || report} />

              {/* Full Transcript */}
              {report.transcript && (
                <Card className="mt-6 border-blue-200" data-testid="parent-call-transcript">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-sanjaya-navy text-base">
                      <MessageSquare className="w-5 h-5 text-blue-600" /> Full Transcript
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                      <p className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">
                        {report.transcript}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Principal's Remarks (highlights + sticky notes) — at the very end */}
              <PrincipalRemarks annotations={report.principal_annotations || {}} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentReportDetail;
